import { useCallback } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useNavigate } from '@mint/routing';

import { CONNECTION_PATHS } from '../connectionPaths.js';
import { markConnectionAuthorized } from '../storage/authorizedConnections.js';

/**
 * For OAuth flows whose provider `redirect_uri` always points at the add
 * route (GA4, client-owned AdForm): a reconnect started on the manage page
 * comes back to the add form, which exchanges the code against the existing
 * connection. Once that exchange succeeds, the reconnect is finished, so the
 * add form calls this to go back to that connection's update page instead of
 * offering to create a new one. Mirrors the legacy
 * `useTraffickingSearchParamsHandler`'s `onConnected`.
 *
 * Replaces the history entry: going back must not land on the add route with
 * the spent `state`/`code` and exchange them again.
 */
export function useOpenReconnectedConnection(platform: string) {
  const navigate = useNavigate();
  const { t } = useTranslation('@mint-lib/connections-ui');

  return useCallback(
    (connectionId: string) => {
      enqueueSnackbar({
        variant: 'success',
        title: t('Account connected:'),
        subTitle: t('Your account has been connected successfully'),
      });
      markConnectionAuthorized(connectionId);
      void navigate({
        to: CONNECTION_PATHS.connectionsUpdate,
        params: { integration: platform, connection: connectionId },
        search: {},
        replace: true,
      });
    },
    [navigate, platform, t],
  );
}
